/**
 * J3「本月剩余可花」的**纯展示逻辑**（唯一一份 · 可花卡与其 spec 共用）
 *
 * 依据：规格 **§12** · `DR-010` 决策三 · J 域 PRD **§4.6** / **§7.3**（`AC-J3.6`–`AC-J3.9`）·
 * 契约 `contract/spendable.ts`（`SPENDABLE_LABEL` / `SpendableResponse`）。
 *
 * ## 🔴 本文件**一个金额都不算、一个日期都不算**
 *
 * | 判定 | 谁定 |
 * |---|---|
 * | 可花是多少（含"无预算"与"已透支"两种非数值态） | 🔴 **服务端**（内核 `composeSpendable`）—— 端侧**不做减法** |
 * | 已花 / 待缴未付 / 必付未付三构成 | 🔴 **服务端**（G 域唯一函数 + F 域同一聚合）—— 端侧**不求和** |
 * | 预算窗口与"今天是哪天" | 🔴 **服务端**（账本时区）—— 端侧**不拼日历** |
 * | 「已占用」百分比 | 🔴 **服务端** `occupiedE4` —— 端侧只**排版**（`percentText`） |
 * | 三构成怎么称呼、无预算说什么 | 本文件（**纯文案**，无判定语义） |
 *
 * ## 🔴 三态各自**谁都不许兜底**（`AC-J3.8` / `AC-J3.9`）
 *
 * - `noBudget` ⇒ 主数值**不存在**（`cents: null`）—— 🔴 **绝不**用 0 兜底：
 *   拿一个空预算算出 `¥0` 会让人以为"这个月一分钱都不能花"；
 * - `overspent` ⇒ 主数值是**透支额**（正数）+ 前缀「已透支」—— 🔴 **不截断为 0**
 *   （截断会把"超了"说成"刚好花完"）；
 * - `available` ⇒ 主数值可为 **0**（真的刚好花完）—— 这与上面第一条的区别必须保住。
 */

import {
  SPENDABLE_FORMULA_TEXT,
  SPENDABLE_LABEL,
  SpendableKind,
  divideExact,
  type SpendableResponse,
  type UpcomingKind,
} from '@jiaying/shared';
import { percentText } from './budget-labels.js';

/** 必付未付的**三构成**中文名（`AC-J3.4`；措辞取自规格 §12 与 J 域 PRD §4.6） */
export const UPCOMING_KIND_LABELS: Readonly<Record<UpcomingKind, string>> = {
  creditCard: '信用卡本期应还',
  payable: '未付待缴',
  schedule: '本窗口剩余期次',
};

/** 无预算时的引导（`AC-J3.8`：**不显示数字**，只给下一步动作） */
export const SPENDABLE_NO_BUDGET_TITLE = '还没设预算';
export const SPENDABLE_NO_BUDGET_HINT =
  '设一个总预算，这里就会告诉你"这个月还能花多少"（已扣掉还没付的账单）。';

/** 已透支的前缀（`AC-J3.9`）—— 只说事实，不做指责（同 G 域文案纪律） */
export const SPENDABLE_OVERSPENT_PREFIX = '已透支';

/**
 * 缺汇率的必付合计不完整提示（架构 §5.4 铁律 5：缺汇率**不静默、不按 1:1 冒充**）。
 *
 * ⚠️ 与 G 域 `FX_INCOMPLETE_BUDGET_NOTE` **刻意分开**：那句讲的是"外币**支出**未计入预算合计"，
 * 这句讲的是"外币**必付**未计入必付合计" —— 两处口径不同，合成一句会让用户搞不清少了哪一块。
 */
export const SPENDABLE_FX_INCOMPLETE_NOTE =
  '部分外币必付项缺少当日汇率，未计入必付合计 —— 数字可能偏小。可在「设置 · 汇率」补录。';

/** 构成明细里的一行 */
export interface SpendableCompositionLine {
  key: 'budget' | 'spent' | 'upcoming' | 'part';
  label: string;
  /** 整数分；`null` = 该行不适用（无预算时的「预算」行） */
  cents: number | null;
  /** 是否缩进（三构成是「必付未付」的子行） */
  indent: boolean;
}

/** 可花卡的**展示就绪**形态 */
export interface SpendableCardModel {
  /** 🔴 唯一名（`AC-J3.1`）—— 取自契约常量，端侧**不重写** */
  title: string;
  /** 口径行（与规格 §12 公式逐字相同） */
  formulaText: string;
  /** 窗口重置说明（形如「10/1 重置」）—— 由服务端 `window.start` **格式化**而来，不是端侧算的日期 */
  resetText: string;
  kind: SpendableKind;
  /** 主数值：`available` = 可花；`overspent` = 透支额；`noBudget` = `null` */
  cents: number | null;
  /** 主数值前缀（`overspent` ⇒ 「已透支」；`available` ⇒ `null`） */
  prefix: string | null;
  /** 主数值是否用**警示色**（只有"已透支"用 —— 可花为 0 仍属正常，`AC-J3.9` 的反面） */
  alarming: boolean;
  /** 构成明细（🔴 全部来自服务端，端侧不求第二次和） */
  composition: readonly SpendableCompositionLine[];
  /** 已花行里的"含待缴"金额（`> 0` 才渲染，`null` = 不显示） */
  unpaidPending: number | null;
  /** 进度条：占用文本与填充宽度%；`noBudget` ⇒ `null`（**不画空进度条**） */
  occupancy: { text: string; percent: number } | null;
  /** 缺汇率提示（`null` = 不显示） */
  fxNotice: string | null;
}

/**
 * 窗口重置说明 —— 🔴 **只格式化，不做日期运算**。
 *
 * `2026-10-01` → `10/1 重置`。⚠️ 不写"距重置还有 N 天"：那需要端侧知道"今天"，
 * 而"今天"是**账本时区**的概念（端侧本地日期在跨时区时可能差一天 —— 与 `DR-004` 同族）。
 */
export function spendableResetText(windowStart: string): string {
  const month = Number(windowStart.slice(5, 7));
  const day = Number(windowStart.slice(8, 10));
  return `${String(month)}/${String(day)} 重置`;
}

/**
 * 服务端视图 → 展示就绪模型。
 *
 * 🔴 三态分支**只看服务端 `kind`**，不自己判 `value < 0`：
 * "有没有预算"与"是不是透支"都是**服务端的判定**（预算行只有服务端看得见）。
 * 端侧若自己判，就会出现"端侧说已透支、服务端说无预算"的分叉。
 */
export function toSpendableCard(view: SpendableResponse): SpendableCardModel {
  const bd = view.breakdown;
  const parts: SpendableCompositionLine[] = (
    ['creditCard', 'payable', 'schedule'] as const
  ).map((kind) => ({
    key: 'part' as const,
    label: UPCOMING_KIND_LABELS[kind],
    cents: bd.upcomingByKind[kind],
    indent: true,
  }));

  const composition: SpendableCompositionLine[] = [
    { key: 'budget', label: '预算', cents: bd.budgetAmount, indent: false },
    { key: 'spent', label: '已花', cents: bd.spent, indent: false },
    { key: 'upcoming', label: '必付未付', cents: bd.upcomingTotal, indent: false },
    ...parts,
  ];

  const unpaidPending = bd.unpaidPending > 0 ? bd.unpaidPending : null;

  return {
    title: SPENDABLE_LABEL,
    formulaText: SPENDABLE_FORMULA_TEXT,
    resetText: spendableResetText(view.window.start),
    kind: view.result.kind,
    cents:
      view.result.kind === SpendableKind.Available
        ? view.result.value
        : view.result.kind === SpendableKind.Overspent
          ? view.result.overspentBy
          : null,
    prefix: view.result.kind === SpendableKind.Overspent ? SPENDABLE_OVERSPENT_PREFIX : null,
    // 🔴 只有"已透支"用警示色；可花为 0（真的花完了）属正常态，不着色
    alarming: view.result.kind === SpendableKind.Overspent,
    composition,
    unpaidPending,
    occupancy:
      view.occupiedE4 === null
        ? null
        : { text: percentText(view.occupiedE4), percent: barPercentOf(view.occupiedE4) },
    fxNotice: view.fxIncomplete ? SPENDABLE_FX_INCOMPLETE_NOTE : null,
  };
}

/**
 * 进度条**填充宽度**（只做展示裁剪：≥100% 填满）。
 *
 * ⚠️ 端侧禁 `/`（定点白名单）⇒ 走共享内核 `divideExact`；与 `BudgetCard.clampPercentOf` 同一手法。
 */
function barPercentOf(e4: number): number {
  if (e4 <= 0) return 0;
  if (e4 >= 10000) return 100;
  return divideExact(e4, 100);
}
